import React, { useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";

export interface RetentionDetailsValue {
  retention_applicable: boolean;
  retentionType: string;
  percentage: number | "";
  retentionAmount: number | "";
  followUpDate: string;
}

interface RetentionDetailsProps {
  value: RetentionDetailsValue;
  onChange: (value: RetentionDetailsValue) => void;
  retentionTypeOptions: { label: string; value: string }[];
  baseAmount?: number; // amount on which retention is calculated
  disabled?: boolean;
  error?: any;
}

const RetentionDetails: React.FC<RetentionDetailsProps> = ({
  value,
  onChange,
  retentionTypeOptions,
  baseAmount = 0,
  disabled = false,
  error,
}) => {
  const handleToggleApplicable = (checked: boolean) => {
    if (!checked) {
      onChange({
        retention_applicable: false,
        retentionType: "",
        percentage: "",
        retentionAmount: "",
        followUpDate: "",
      });
      return;
    }

    onChange({
      ...value,
      retention_applicable: true,
    });
  };

  const handleChange = (field: keyof RetentionDetailsValue, fieldValue: any) => {
    let updated: RetentionDetailsValue = {
      ...value,
      [field]: fieldValue,
    };

    // Cap percentage at 100% and prevent negatives
    if (field === "percentage") {
      let pct = Number(fieldValue) || 0;
      // Cap at 100 and prevent negatives
      pct = Math.min(Math.max(0, pct), 100);
      updated.percentage = pct;
      
      // auto-calculate retention amount when percentage changes
      if (baseAmount) {
        updated.retentionAmount = Number(((baseAmount * pct) / 100).toFixed(2));
      }
    }

    // If retention type changes to "amount", clear the auto-calculated amount
    if (field === "retentionType" && fieldValue === "amount") {
      updated.retentionAmount = "";
    }

    onChange(updated);
  };

  const handlePercentageBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    let newValue = parseFloat(e.target.value);
    if (isNaN(newValue)) {
      newValue = 0;
    }
    // Cap at 100 and prevent negatives on blur
    newValue = Math.min(Math.max(0, newValue), 100);
    handleChange("percentage", newValue);
  };

  // Recalculate retention amount whenever baseAmount or percentage changes (if retentionType is "percentage")
  useEffect(() => {
    if (
      value.retention_applicable &&
      value.retentionType === "percentage" &&
      baseAmount &&
      value.percentage !== "" &&
      value.percentage !== null &&
      value.percentage !== undefined
    ) {
      let pct = Number(value.percentage) || 0;
      // Ensure percentage doesn't exceed 100
      if (pct > 100) {
        pct = 100;
        onChange({
          ...value,
          percentage: 100,
        });
      }
      const calculatedAmount = Number(((baseAmount * pct) / 100).toFixed(2));
      
      // Only update if the calculated amount is different from current value
      if (calculatedAmount !== Number(value.retentionAmount)) {
        onChange({
          ...value,
          percentage: pct,
          retentionAmount: calculatedAmount,
        });
      }
    }
  }, [baseAmount, value.percentage, value.retentionType, value.retention_applicable]);

  // console.log("value in RetentionDetails", value);

  return (
    <div className="">
      <div className="d-flex align-items-center mb-3">
        <h5 className="mb-3 fw-semibold mb-3 align-items-center">
        <Form.Check
          type="checkbox"
          label="Retention Details"
          checked={!!value.retention_applicable}
          disabled={disabled}
          onChange={(e) => handleToggleApplicable(e.target.checked)}
        />
        </h5>
      </div>

      {value.retention_applicable ? (
        <Row className="g-3">
          <Col md={3}>
            <Form.Group>
              <Form.Label>Retention Type</Form.Label>
              <Form.Select
                value={value.retentionType}
                disabled={disabled}
                onChange={(e) => handleChange("retentionType", e.target.value)}
              >
                <option value="">Select</option>
                {retentionTypeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </Col>

          {value.retentionType === "percentage" && (
            <Col md={3}>
              <Form.Group>
                <Form.Label>Percentage</Form.Label>
                <Form.Control
                  type="number"
                  placeholder="Enter Value"
                  value={value.percentage}
                  onChange={(e) => handleChange("percentage", e.target.value)}
                  onBlur={handlePercentageBlur}
                  min="0"
                  max="100"
                  isInvalid={!!error?.percentage}
                  disabled={disabled}
                />
                {error?.percentage && (
                  <Form.Control.Feedback type="invalid">
                    {error.percentage}
                  </Form.Control.Feedback>
                )}
                {value.percentage !== "" && value.percentage !== null && value.percentage !== undefined && Number(value.percentage) > 100 && (
                  <Form.Text className="text-warning">
                    Percentage capped at 100%.
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
          )}

          <Col md={3}>
            <Form.Group>
              <Form.Label>Retention Amount</Form.Label>
              <Form.Control
                type="number"
                placeholder={
                  value.retentionType === "percentage"
                    ? "(Auto Calculated)"
                    : "Enter Amount"
                }
                value={
                  value.retentionType === "percentage"
                    ? (value.retentionAmount !== "" && value.retentionAmount !== null && value.retentionAmount !== undefined
                        ? Number(value.retentionAmount).toFixed(2)
                        : "")
                    : (value.retentionAmount !== "" && value.retentionAmount !== null && value.retentionAmount !== undefined
                        ? value.retentionAmount
                        : "")
                }
                disabled={disabled || value.retentionType === "percentage"}
                onChange={(e) =>
                  value.retentionType !== "percentage" &&
                  handleChange("retentionAmount", e.target.value)
                }
              />
            </Form.Group>
          </Col>

          <Col md={3}>
            <Form.Group>
              <Form.Label>Follow Up Date</Form.Label>
              <Form.Control
                type="date"
                value={value.followUpDate}
                disabled={disabled}
                onChange={(e) => handleChange("followUpDate", e.target.value)}
              />
            </Form.Group>
          </Col>
        </Row>
      ) : null}

      {error ? (
        <div className="text-danger mt-2" style={{ fontSize: "0.875rem" }}>
          {typeof error === "string" ? error : "Invalid retention details"}
        </div>
      ) : null}
    </div>
  );
};

export default RetentionDetails;
